import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useAccent } from '../context/AccentContext';
import { UserManagementPanel } from 'auth-sso-kit/client';
import { TabBar } from '../components/TabBar';
import { useT, useFormatDate } from '../i18n';

export function AdminPage() {
  const { t } = useT();
  const [tab, setTab] = useState('users');

  const TABS = [
    { key: 'users', label: t('admin.tab.users') },
    { key: 'roles', label: t('admin.tab.roles') },
    { key: 'staffmap', label: t('admin.tab.staffmap') },  // identity tab (V2.1)
    { key: 'settings', label: t('admin.tab.settings') },
  ];

  return (
    <div style={{ maxWidth: 900 }}>
      <h1 style={{ fontSize: 'var(--t-17)', fontWeight: 700, marginBottom: 'var(--s16)' }}>{t('admin.title')}</h1>
      <TabBar tabs={TABS} activeTab={tab} onTabChange={setTab} />

      {tab === 'users' && (
        <section>
          <UserManagementPanel />
        </section>
      )}
      {tab === 'roles' && <RolesTab />}
      {tab === 'staffmap' && <StaffMapTab />}
      {tab === 'settings' && <SettingsTab />}
    </div>
  );
}

const CAP_COLUMNS: { key: string; labelKey: string; hintKey: string }[] = [
  { key: 'is_admin',       labelKey: 'admin.cap.admin.label',     hintKey: 'admin.cap.admin.hint' },
  { key: 'is_resp_rm_oai', labelKey: 'admin.cap.respRmOai.label', hintKey: 'admin.cap.respRmOai.hint' },
  { key: 'is_resp_rm_ri',  labelKey: 'admin.cap.respRmRi.label',  hintKey: 'admin.cap.respRmRi.hint' },
  { key: 'is_resp_rp',     labelKey: 'admin.cap.respRp.label',    hintKey: 'admin.cap.respRp.hint' },
  { key: 'is_rm_oai',      labelKey: 'admin.cap.rmOai.label',     hintKey: 'admin.cap.rmOai.hint' },
  { key: 'is_rm_ri',       labelKey: 'admin.cap.rmRi.label',      hintKey: 'admin.cap.rmRi.hint' },
  { key: 'is_rp_oai',      labelKey: 'admin.cap.rpOai.label',     hintKey: 'admin.cap.rpOai.hint' },
  { key: 'is_rp_ri',       labelKey: 'admin.cap.rpRi.label',      hintKey: 'admin.cap.rpRi.hint' },
];

function RolesTab() {
  const { t } = useT();
  const fd = useFormatDate();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (api.getRoleUsers() as Promise<any[]>).then(setUsers).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleToggle = async (userId: number, key: string, next: boolean) => {
    // Optimistic flip; revert on failure.
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, [key]: next } : u));
    try {
      await api.updateUserCaps(userId, { [key]: next });
    } catch (err: any) {
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, [key]: !next } : u));
      alert(err.message);
    }
  };

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <p style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)', marginBottom: 'var(--s12)' }}>
        {t('admin.roles.introBefore')} <em>RM</em>/<em>RP</em> {t('admin.roles.introAfter')}
      </p>
      <div style={{ overflowX: 'auto' }}>
        <table className="table table-compact" style={{ fontSize: 'var(--t-12)' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>{t('admin.roles.colName')}</th>
              <th style={{ textAlign: 'left', color: 'var(--ink-3)' }}>{t('users.col.email')}</th>
              {CAP_COLUMNS.map(c => (
                <th key={c.key} title={t(c.hintKey)} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {t(c.labelKey)}
                </th>
              ))}
              <th style={{ textAlign: 'left', color: 'var(--ink-3)' }}>{t('admin.roles.lastLogin')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{u.name}</td>
                <td style={{ color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{u.email}</td>
                {CAP_COLUMNS.map(c => (
                  <td key={c.key} style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={!!u[c.key]}
                      onChange={(e) => handleToggle(u.id, c.key, e.target.checked)}
                      style={{ width: 14, height: 14, accentColor: 'var(--accent)', cursor: 'pointer' }}
                    />
                  </td>
                ))}
                <td style={{ color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                  {u.last_login ? fd.medium(u.last_login) : t('admin.roles.never')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StaffMapTab() {
  const { t } = useT();
  // V2.1: identity is user_account (employee ↔ login) + native roles; this
  // tab is a read-only view of session users and their V2.1 accounts.
  // Mapping happens at first login (bootstrap) or by provisioning in V2.1.
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    (api.get('/admin/identity') as Promise<any[]>)
      .then(setRows)
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="spinner" />;

  return (
    <div>
      <p className="text-ink-3" style={{ marginBottom: 'var(--s12)', fontSize: 'var(--t-13)' }}>
        {t('admin.staffmap.intro')}
      </p>

      <table className="data-table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>{t('admin.staffmap.userLabel')}</th>
            <th>{t('admin.staffmap.colStaff')}</th>
            <th>{t('admin.identity.roles')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r: any) => (
            <tr key={r.id}>
              <td>{r.name} <span className="text-ink-3">({r.email})</span></td>
              <td>
                {r.v21
                  ? `${r.v21.first_name} ${r.v21.last_name}`
                  : <span className="text-ink-3">{t('admin.identity.noAccount')}</span>}
              </td>
              <td>
                {(r.v21?.roles ?? []).map((role: string) => (
                  <span key={role} className="badge" style={{ marginRight: 4 }}>{role}</span>
                ))}
                {(r.grants ?? []).map((g: string) => (
                  <span key={g} className="badge" style={{ marginRight: 4, opacity: 0.75 }}>{g}</span>
                ))}
                {!r.v21 && (r.grants ?? []).length === 0 && <span className="text-ink-3">—</span>}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={3} className="text-ink-3">{t('admin.staffmap.empty')}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SettingsTab() {
  const { t } = useT();
  const { accent, setAccent, logoUrl, refreshSettings } = useAccent();
  const [color, setColor] = useState(accent);

  // AI settings
  const [aiUrl, setAiUrl] = useState('');
  const [aiModel, setAiModel] = useState('');
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [aiLoading, setAiLoading] = useState(true);
  const [aiSaved, setAiSaved] = useState(false);
  const [aiError, setAiError] = useState('');

  useEffect(() => {
    // Load current settings
    (api.getSettings() as Promise<Record<string, string>>).then(s => {
      setAiUrl(s.ai_api_url || '');
      setAiModel(s.ai_model || '');
    }).catch(() => {});

    // Fetch available models from the proxy
    (api.get('/admin/ai-models') as Promise<any>).then(data => {
      setAvailableModels(data.models || []);
      if (data.error) setAiError(data.error);
    }).catch(() => {}).finally(() => setAiLoading(false));
  }, []);

  const handleColorSave = async () => {
    await setAccent(color);
  };

  const handleAiSave = async () => {
    setAiSaved(false);
    setAiError('');
    try {
      if (aiUrl) await api.updateSetting('ai_api_url', aiUrl);
      if (aiModel) await api.updateSetting('ai_model', aiModel);
      setAiSaved(true);
      setTimeout(() => setAiSaved(false), 2000);

      // Refresh models list with new URL
      setAiLoading(true);
      const data = await api.get('/admin/ai-models') as any;
      setAvailableModels(data.models || []);
      if (data.error) setAiError(data.error);
      setAiLoading(false);
    } catch (err: any) {
      setAiError(err.message);
    }
  };

  const handleRefreshModels = async () => {
    setAiLoading(true);
    setAiError('');
    try {
      // Save URL first if changed
      if (aiUrl) await api.updateSetting('ai_api_url', aiUrl);
      const data = await api.get('/admin/ai-models') as any;
      setAvailableModels(data.models || []);
      if (data.error) setAiError(data.error);
    } catch (err: any) {
      setAiError(err.message);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s24)' }}>
      {/* Accent Color */}
      <section>
        <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 600, marginBottom: 'var(--s8)' }}>{t('admin.settings.accentColor')}</h2>
        <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center' }}>
          <input type="color" value={color} onChange={e => setColor(e.target.value)} style={{ width: 48, height: 32, border: 'none', cursor: 'pointer' }} />
          <input className="form-input" value={color} onChange={e => setColor(e.target.value)} style={{ maxWidth: 120 }} />
          <button className="btn btn-accent btn-sm" onClick={handleColorSave}>{t('common.save')}</button>
        </div>
      </section>

      {/* Logo */}
      <section>
        <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 600, marginBottom: 'var(--s8)' }}>{t('admin.settings.logo')}</h2>
        <div style={{ display: 'flex', gap: 'var(--s12)', alignItems: 'center' }}>
          {logoUrl ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s12)' }}>
              <img src={logoUrl} alt={t('admin.settings.logoAlt')} style={{ height: 32 }} />
              <button className="btn btn-ghost btn-sm text-err" onClick={async () => {
                await fetch('/api/admin/logo', { method: 'DELETE', credentials: 'include' });
                refreshSettings();
              }}>{t('common.delete')}</button>
            </div>
          ) : (
            <p style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)' }}>{t('admin.settings.noLogo')}</p>
          )}
          <label className="btn btn-accent btn-sm" style={{ cursor: 'pointer' }}>
            {logoUrl ? t('admin.settings.logoReplace') : t('admin.settings.logoUpload')}
            <input type="file" accept="image/png" style={{ display: 'none' }} onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = async () => {
                const base64 = (reader.result as string).split(',')[1];
                await fetch('/api/admin/logo', {
                  method: 'POST', credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ data: base64 }),
                });
                refreshSettings();
              };
              reader.readAsDataURL(file);
              e.target.value = '';
            }} />
          </label>
        </div>
      </section>

      {/* AI Configuration */}
      <section>
        <h2 style={{ fontSize: 'var(--t-15)', fontWeight: 600, marginBottom: 'var(--s8)' }}>{t('admin.settings.aiConfig')}</h2>
        <p style={{ fontSize: 'var(--t-12)', color: 'var(--ink-3)', marginBottom: 'var(--s12)' }}>
          {t('admin.settings.aiIntro')}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s12)' }}>
          <div className="form-group">
            <label className="form-label">{t('admin.settings.apiUrl')}</label>
            <input
              className="form-input"
              value={aiUrl}
              onChange={e => setAiUrl(e.target.value)}
              placeholder="http://192.168.10.9:4000/v1"
              style={{ maxWidth: 400 }}
            />
            <p style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)', marginTop: 2 }}>
              {t('admin.settings.apiUrlDefault')}
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">{t('admin.settings.model')}</label>
            <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center' }}>
              {availableModels.length > 0 ? (
                <select
                  className="form-input"
                  value={aiModel}
                  onChange={e => setAiModel(e.target.value)}
                  style={{ maxWidth: 400 }}
                >
                  <option value="">{t('admin.settings.selectModel')}</option>
                  {availableModels.map((m: any) => (
                    <option key={m.id} value={m.id}>{m.id}</option>
                  ))}
                </select>
              ) : (
                <input
                  className="form-input"
                  value={aiModel}
                  onChange={e => setAiModel(e.target.value)}
                  placeholder="cyankiwi/Qwen3.5-122B-A10B-AWQ-4bit"
                  style={{ maxWidth: 400 }}
                />
              )}
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleRefreshModels}
                disabled={aiLoading}
              >
                {aiLoading ? '...' : t('admin.settings.refresh')}
              </button>
            </div>
            {availableModels.length > 0 && (
              <p style={{ fontSize: 'var(--t-11)', color: 'var(--ok)', marginTop: 2 }}>
                {t('admin.settings.modelsAvailable', { n: availableModels.length })}
              </p>
            )}
          </div>

          {aiError && (
            <div style={{ fontSize: 'var(--t-12)', color: 'var(--err)' }}>{aiError}</div>
          )}

          <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center' }}>
            <button className="btn btn-accent btn-sm" onClick={handleAiSave}>
              {aiSaved ? t('admin.settings.saved') : t('admin.settings.saveAi')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
